import { useCallback, useState } from "react";
import { Toast } from "primereact/toast";

interface ToastActionParams {
  action: () => Promise<any>;
  successMessage: string;
  // A function when the message depends on why the action failed.
  errorMessage: string | ((err: unknown) => string);
}

interface ToastActionWrapper {
  toastActionWrapper: (params: ToastActionParams) => Promise<boolean>;
  isLoading: boolean;
}

const useToastActionWrapper = (
  toastRef: React.RefObject<Toast>
): ToastActionWrapper => {
  const [isLoading, setIsLoading] = useState(false);

  const toastActionWrapper = useCallback(
    async ({ action, successMessage, errorMessage }: ToastActionParams): Promise<boolean> => {
      setIsLoading(true);
      try {
        await action();
        if (toastRef.current) {
          toastRef.current.show({
            severity: "success",
            summary: "Success",
            detail: successMessage,
            life: 3000,
          });
        }
        return true;
      } catch (err) {
        console.error("Toast action error:", err);
        if (toastRef.current) {
          toastRef.current.show({
            severity: "error",
            summary: "Error",
            detail: typeof errorMessage === "function" ? errorMessage(err) : errorMessage,
            life: 3000,
          });
        }
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, toastRef]
  );

  return { toastActionWrapper, isLoading };
};

export default useToastActionWrapper;
